import { useCallback, useEffect, useMemo, useState } from 'react'
import { Popup, type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import { fitToFeatures, tokens } from '@agribot/polygon-checks-ui'
import { MapFrame } from '@agribot/sourcing-model'
import type { OriginImpact } from '../scenario'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
const LEGEND = [
  { colour: tokens.color.brandTeal, label: 'Not affected' },
  { colour: tokens.color.dataRisk.med, label: 'Reduced' },
  { colour: tokens.color.danger, label: 'Stopped' },
]
const tonnes = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: 0 })} t`

/**
 * The campaign's origins sized by the volume they supply, coloured by what
 * the scenario does to them. Hover for the figures; the legend says it in
 * words, so colour is never the only signal.
 */
export function ScenarioMap({ origins }: { origins: OriginImpact[] }) {
  const [map, setMap] = useState<MapLibreMap | null>(null)
    const max = Math.max(1, ...origins.map((o) => o.totalT))

  const features = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: origins.filter((o) => o.lng !== null && o.lat !== null).map((o) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [o.lng!, o.lat!] },
      properties: {
        name: o.origin, country: o.country, total: o.totalT, lost: o.lostT,
        radius: 6 + 16 * Math.sqrt(o.totalT / max),
        colour: o.reduction >= 0.999 ? tokens.color.danger : o.reduction > 0 ? tokens.color.dataRisk.med : tokens.color.brandTeal,
      },
    })),
  }), [origins, max])

  const onReady = useCallback((instance: MapLibreMap) => {
    instance.addSource('impact', { type: 'geojson', data: EMPTY })
    instance.addLayer({
      id: 'impact', type: 'circle', source: 'impact',
      paint: {
        'circle-radius': ['get', 'radius'], 'circle-color': ['get', 'colour'], 'circle-opacity': 0.75,
        'circle-stroke-color': tokens.color.ink0, 'circle-stroke-width': 1.5,
      },
    })
    const popup = new Popup({ closeButton: false, closeOnClick: false, offset: 10 })
    instance.on('mousemove', 'impact', (e) => {
      const p = e.features?.[0]?.properties as { name: string; country: string; total: number; lost: number } | undefined
      if (!p) return
      instance.getCanvas().style.cursor = 'pointer'
      popup.setLngLat(e.lngLat)
        .setHTML(`<strong>${escape(p.name)}</strong> (${escape(p.country)})<br/>${tonnes(p.total)} a year${p.lost ? `, ${tonnes(p.lost)} lost` : ''}`)
        .addTo(instance)
    })
    instance.on('mouseleave', 'impact', () => { instance.getCanvas().style.cursor = ''; popup.remove() })
    setMap(instance)
  }, [])

  useEffect(() => { (map?.getSource('impact') as GeoJSONSource | undefined)?.setData(features) }, [map, features])
  useEffect(() => { if (map) fitToFeatures(map, features, 48) /* once, when the map is ready */ }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MapFrame
      label="Map of origins by scenario impact"
      map={map}
      onReady={onReady}
      onRemove={() => setMap(null)}
      legend={[...LEGEND.map((l) => ({ label: l.label, swatch: { borderRadius: '50%', backgroundColor: l.colour } })), { label: 'Size: volume supplied' }]}
      initialCentre={[-30, -5]}
      initialZoom={1.5}
      height={360}
    />
  )
}

const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
